import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';
import { createTeacherSummary } from './generate-grading-reports.js';

/**
 * Lightweight per-exam summary for a class's exam overview (see ClassRoster in classroom-page.tsx).
 * Computed server-side from the full exam/submission trees via {@link createTeacherSummary} — the
 * same aggregation the grading-report export uses — so the client only ever receives the small
 * rolled-up numbers, never the heavy task/submission trees themselves.
 */
@Entity()
export class ClassExamSummaryDTO extends AdornisEntity {
  static override _class = 'ClassExamSummaryDTO';

  @Field(() => String)
  conversationId!: string;

  @Field(() => String)
  title: Maybe<string>;

  @Field(() => String)
  subject: Maybe<string>;

  @Field(() => String)
  status!: string;

  @Field(() => String)
  createdAt!: string;

  @Field(() => Int)
  submissionCount!: number;

  @Field(() => Float)
  averagePercentage!: number;

  @Field(() => Float)
  averageGrade: Maybe<number>;
}

export const getClassExamOverviewQuery = registerQuery({
  operationName: 'getClassExamOverviewQuery',
  type: () => [ClassExamSummaryDTO],
  params: [{ name: 'className', type: () => String }],

  resolve:
    (className: string) =>
    async (_selection: BaseQLSelectionSet<ClassExamSummaryDTO>): Promise<ClassExamSummaryDTO[]> => {
      const trimmedClass = typeof className === 'string' ? className.trim() : '';
      if (!trimmedClass) return [];

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) return [];

      const examCollection = await getCollection(Exam);
      const submissionCollection = await getCollection(Submission);

      const exams = await examCollection
        .find({ userID: currentUser._id, class: trimmedClass })
        .sort({ createdAt: -1 })
        .toArray();

      const results: ClassExamSummaryDTO[] = [];
      for (const exam of exams) {
        const submissions = await submissionCollection.find({ examId: exam._id }).toArray();

        let averagePercentage = 0;
        let averageGrade: number | null = null;
        if (submissions.length > 0) {
          const summary = createTeacherSummary(exam, submissions as any[]);
          averagePercentage = summary.averagePercentage;
          averageGrade = summary.averageGrade;
        }

        results.push(
          new ClassExamSummaryDTO({
            _id: exam._id,
            conversationId: exam.conversationId,
            title: exam.title,
            subject: exam.subject,
            status: exam.status,
            createdAt: exam.createdAt,
            submissionCount: submissions.length,
            averagePercentage,
            averageGrade,
          }),
        );
      }

      return results;
    },
});
